<template>
  <div ref="root" class="ticket-taxonomy">
    <section class="ticket-taxonomy__group">
      <span class="ticket-taxonomy__title">Tags</span>
      <div class="ticket-taxonomy__items">
        <span
          v-for="tag in selectedTags"
          :key="`tag-${tag.id}`"
          class="ticket-taxonomy__chip"
          :style="chipStyle(tag)"
        >
          <button type="button" class="ticket-taxonomy__chip-name" :disabled="working" :aria-label="`Edit tag ${tag.name}`" @click.stop="openEditor('tags', tag)">{{ tag.name }}</button>
          <button type="button" class="ticket-taxonomy__remove" :disabled="working" :aria-label="`Remove tag ${tag.name}`" @click.stop="remove('tags', tag)">×</button>
          <div v-if="isColorTarget('tags', tag)" class="ticket-taxonomy__popover ticket-taxonomy__popover--colors" @click.stop>
            <label class="ticket-taxonomy__editor-name"><span>Name</span><input v-model="editName" type="text" maxlength="64" :disabled="editWorking" @keydown.enter.prevent="changeName('tags', tag)" @blur="changeName('tags', tag)"></label>
            <strong>Tag color</strong>
            <div class="ticket-taxonomy__palette" role="list" aria-label="Tag colors">
              <button
                v-for="color in palette"
                :key="color"
                type="button"
                :style="{ backgroundColor: color }"
                :class="{ 'is-selected': normalizedColor(tag.color) === normalizedColor(color) }"
                :aria-label="`Use color ${color}`"
                :disabled="editWorking"
                @mousedown.prevent
                @click="changeColor('tags', tag, color)"
              ></button>
            </div>
          </div>
        </span>
        <div class="ticket-taxonomy__add-wrap">
          <button type="button" class="ticket-taxonomy__add" :disabled="working" @click.stop="openPicker('tags')">+ Add tag</button>
          <tag-picker-popover
            v-if="pickerKind === 'tags'"
            title="Add tag"
            singular="tag"
            :options="availableTags"
            :palette="palette"
            :working="pickerWorking"
            @select="selectExisting('tags', $event)"
            @create="createAndSelect('tags', $event)"
            @close="closePopovers"
          />
        </div>
      </div>
    </section>

    <span class="ticket-taxonomy__divider" aria-hidden="true"></span>

    <section class="ticket-taxonomy__group ticket-taxonomy__group--responsibility">
      <span class="ticket-taxonomy__title">Responsibility</span>
      <div class="ticket-taxonomy__items">
        <span
          v-for="label in selectedLabels"
          :key="`label-${label.id}`"
          class="ticket-taxonomy__chip"
          :style="chipStyle(label)"
        >
          <button type="button" class="ticket-taxonomy__chip-name" :disabled="working" :aria-label="`Edit responsibility ${label.name}`" @click.stop="openEditor('responsible_labels', label)">{{ label.name }}</button>
          <button type="button" class="ticket-taxonomy__remove" :disabled="working" :aria-label="`Remove responsibility ${label.name}`" @click.stop="remove('responsible_labels', label)">×</button>
          <div v-if="isColorTarget('responsible_labels', label)" class="ticket-taxonomy__popover ticket-taxonomy__popover--colors" @click.stop>
            <label class="ticket-taxonomy__editor-name"><span>Name</span><input v-model="editName" type="text" maxlength="64" :disabled="editWorking" @keydown.enter.prevent="changeName('responsible_labels', label)" @blur="changeName('responsible_labels', label)"></label>
            <strong>Responsibility color</strong>
            <div class="ticket-taxonomy__palette" role="list" aria-label="Responsibility colors">
              <button
                v-for="color in palette"
                :key="color"
                type="button"
                :style="{ backgroundColor: color }"
                :class="{ 'is-selected': normalizedColor(label.color) === normalizedColor(color) }"
                :aria-label="`Use color ${color}`"
                :disabled="editWorking"
                @mousedown.prevent
                @click="changeColor('responsible_labels', label, color)"
              ></button>
            </div>
          </div>
        </span>
        <div class="ticket-taxonomy__add-wrap">
          <button type="button" class="ticket-taxonomy__add ticket-taxonomy__add--responsibility" :disabled="working" @click.stop="openPicker('responsible_labels')">+ Add responsibility</button>
          <tag-picker-popover
            v-if="pickerKind === 'responsible_labels'"
            title="Add responsibility"
            singular="responsibility"
            :options="availableLabels"
            :palette="palette"
            :working="pickerWorking"
            @select="selectExisting('responsible_labels', $event)"
            @create="createAndSelect('responsible_labels', $event)"
            @close="closePopovers"
          />
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { patchSupportTicketDictionary, postSupportTicketDictionary } from '@/http'
import { apiError } from '../ticketState'
import { SUPPORT_TAG_COLORS } from './tagPalette'

const TagPickerPopover = {
  name: 'SupportTicketTagPickerPopover',
  props: {
    title: { type: String, required: true },
    singular: { type: String, required: true },
    options: { type: Array, default: () => [] },
    palette: { type: Array, required: true },
    working: Boolean
  },
  data() {
    return { query: '', color: this.palette[7] }
  },
  computed: {
    filteredOptions() {
      const query = this.query.trim().toLocaleLowerCase()
      return this.options.filter(option => !query || option.name.toLocaleLowerCase().includes(query)).slice(0, 8)
    },
    exactOption() {
      const query = this.query.trim().toLocaleLowerCase()
      return query ? this.options.find(option => option.name.toLocaleLowerCase() === query) : null
    },
    canCreate() { return Boolean(this.query.trim()) && !this.exactOption }
  },
  mounted() { this.$nextTick(() => this.$refs.input?.focus()) },
  methods: {
    submit() {
      if (this.working) return
      if (this.exactOption) this.$emit('select', this.exactOption)
      else if (this.canCreate) this.$emit('create', { name: this.query.trim(), color: this.color })
    }
  },
  template: `
    <div class="ticket-taxonomy__popover ticket-taxonomy__popover--picker" @click.stop>
      <header><strong>{{ title }}</strong><button type="button" aria-label="Close" @click="$emit('close')">×</button></header>
      <input ref="input" v-model="query" type="text" :placeholder="'Search or create ' + singular" :disabled="working" @keydown.enter.prevent="submit">
      <div class="ticket-taxonomy__picker-palette" aria-label="Color for a new item">
        <button v-for="item in palette" :key="item" type="button" :style="{ backgroundColor: item }" :class="{ 'is-selected': color === item }" :aria-label="'Use color ' + item" @click="color = item"></button>
      </div>
      <div class="ticket-taxonomy__options">
        <button v-for="option in filteredOptions" :key="option.id" type="button" :disabled="working" @click="$emit('select', option)">
          <i :style="{ backgroundColor: option.color }"></i><span>{{ option.name }}</span>
        </button>
        <button v-if="canCreate" type="button" class="ticket-taxonomy__create" :disabled="working" @click="submit">
          <i :style="{ backgroundColor: color }"></i><span>Create “{{ query.trim() }}”</span><kbd>Enter</kbd>
        </button>
        <p v-if="!filteredOptions.length && !canCreate">Start typing to create a new {{ singular }}.</p>
      </div>
    </div>
  `
}

export default {
  name: 'SupportTicketTagControls',
  components: { TagPickerPopover },
  props: {
    selectedTags: { type: Array, default: () => [] },
    selectedLabels: { type: Array, default: () => [] },
    tags: { type: Array, default: () => [] },
    labels: { type: Array, default: () => [] },
    working: Boolean
  },
  data() {
    return {
      palette: SUPPORT_TAG_COLORS,
      pickerKind: null,
      colorTarget: null,
      editName: '',
      pickerWorking: false,
      editWorking: false
    }
  },
  computed: {
    availableTags() { return this.available(this.tags, this.selectedTags) },
    availableLabels() { return this.available(this.labels, this.selectedLabels) }
  },
  mounted() { document.addEventListener('click', this.handleOutsideClick) },
  beforeDestroy() { document.removeEventListener('click', this.handleOutsideClick) },
  methods: {
    available(options, selected) {
      const selectedIds = new Set(selected.map(item => item.id))
      return options.filter(item => !selectedIds.has(item.id))
    },
    normalizedColor(color) { return (color || '').toUpperCase() },
    chipStyle(item) {
      const color = item?.color || this.palette[11]
      return { backgroundColor: color, borderColor: color }
    },
    selected(kind) { return kind === 'tags' ? this.selectedTags : this.selectedLabels },
    options(kind) { return kind === 'tags' ? this.tags : this.labels },
    eventName(kind) { return kind === 'tags' ? 'update-tags' : 'update-labels' },
    openPicker(kind) {
      this.colorTarget = null
      this.pickerKind = this.pickerKind === kind ? null : kind
    },
    openEditor(kind, item) {
      this.pickerKind = null
      if (this.isColorTarget(kind, item)) {
        this.colorTarget = null
        return
      }
      this.colorTarget = { kind, id: item.id }
      this.editName = item.name
    },
    isColorTarget(kind, item) { return this.colorTarget?.kind === kind && this.colorTarget.id === item.id },
    closePopovers() { this.pickerKind = null; this.colorTarget = null; this.editName = '' },
    handleOutsideClick(event) {
      if (this.$refs.root && !this.$refs.root.contains(event.target)) this.closePopovers()
    },
    remove(kind, item) {
      if (this.working) return
      this.closePopovers()
      this.$emit(this.eventName(kind), this.selected(kind).filter(value => value.id !== item.id))
    },
    selectExisting(kind, item) {
      if (this.working || this.pickerWorking) return
      this.$emit(this.eventName(kind), [...this.selected(kind), item])
      this.closePopovers()
    },
    async createAndSelect(kind, value) {
      if (this.working || this.pickerWorking) return
      this.pickerWorking = true
      try {
        const response = await postSupportTicketDictionary(kind, value)
        this.$emit('dictionary-created', { kind, item: response.data })
        this.$emit(this.eventName(kind), [...this.selected(kind), response.data])
        this.closePopovers()
      } catch (error) {
        this.$notify({ type: 'error', text: apiError(error) })
      } finally {
        this.pickerWorking = false
      }
    },
    changeName(kind, item) {
      const name = this.editName.trim()
      if (!name || name === item.name || this.editWorking) return
      this.updateItem(kind, item, { name })
    },
    changeColor(kind, item, color) {
      if (this.editWorking || this.normalizedColor(item.color) === this.normalizedColor(color)) return
      const payload = { color }
      const name = this.editName.trim()
      if (name && name !== item.name) payload.name = name
      this.updateItem(kind, item, payload)
    },
    async updateItem(kind, item, payload) {
      this.editWorking = true
      try {
        const response = await patchSupportTicketDictionary(kind, item.id, payload)
        this.editName = response.data.name
        this.$emit('dictionary-updated', { kind, item: response.data })
      } catch (error) {
        this.$notify({ type: 'error', text: apiError(error) })
      } finally {
        this.editWorking = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.ticket-taxonomy { display: flex; min-width: 0; align-items: center; flex-wrap: wrap; gap: 10px; }
.ticket-taxonomy__group { display: flex; min-width: 0; align-items: center; flex-wrap: wrap; gap: 8px; }
.ticket-taxonomy__title { color: $ui-muted; font-size: 11px; font-weight: 700; letter-spacing: .04em; text-transform: uppercase; }
.ticket-taxonomy__items { display: flex; min-width: 0; align-items: center; flex-wrap: wrap; gap: 6px; }
.ticket-taxonomy__divider { width: 1px; height: 24px; background: $ui-line-strong; }
.ticket-taxonomy__chip { position: relative; display: inline-flex; min-height: 28px; align-items: stretch; border: 1px solid; border-radius: 8px; color: #fff; font-size: 12px; font-weight: 600; line-height: 18px; }
.ticket-taxonomy__chip button { color: inherit; font: inherit; }
.ticket-taxonomy__chip-name { padding: 4px 7px 4px 9px; border: 0; border-radius: 7px 0 0 7px; background: transparent; cursor: pointer; }
.ticket-taxonomy__remove { width: 25px; padding: 0; border: 0; border-left: 1px solid rgba(255, 255, 255, .34); border-radius: 0 7px 7px 0; background: transparent; cursor: pointer; font-size: 16px !important; }
.ticket-taxonomy__chip-name:hover,
.ticket-taxonomy__remove:hover { background: rgba(0, 0, 0, .13); }
.ticket-taxonomy__chip button:disabled { cursor: wait; opacity: .65; }
.ticket-taxonomy__add-wrap { position: relative; }
.ticket-taxonomy__add { min-height: 30px; padding: 0 10px; border: 1px dashed $ui-line-strong; border-radius: 8px; background: $ui-surface-soft; color: $ui-blue; cursor: pointer; font-size: 12px; font-weight: 600; }
.ticket-taxonomy__add--responsibility { color: #6941c6; }
.ticket-taxonomy__add:hover { border-color: currentColor; background: $ui-blue-soft; }
.ticket-taxonomy__add:disabled { cursor: wait; opacity: .55; }
.ticket-taxonomy__popover { position: absolute; z-index: 45; width: 286px; padding: 12px; border: 1px solid $ui-line; border-radius: 12px; background: $ui-surface; box-shadow: 0 14px 36px rgba(28, 45, 74, .18); color: $ui-ink; }
.ticket-taxonomy__popover--colors { top: calc(100% + 8px); left: 0; }
.ticket-taxonomy__popover--colors > strong { display: block; margin-bottom: 10px; font-size: 12px; }
.ticket-taxonomy__editor-name { display: grid; gap: 5px; margin-bottom: 10px; }
.ticket-taxonomy__editor-name > span { color: $ui-muted; font-size: 10px; font-weight: 700; letter-spacing: .04em; text-transform: uppercase; }
.ticket-taxonomy__popover--picker { top: calc(100% + 8px); left: 0; }
.ticket-taxonomy__popover :deep(header) { display: flex; align-items: center; justify-content: space-between; margin-bottom: 10px; }
.ticket-taxonomy__popover :deep(header strong) { font-size: 13px; }
.ticket-taxonomy__popover :deep(header button) { padding: 0; border: 0; background: transparent; color: $ui-muted; cursor: pointer; font-size: 20px; }
.ticket-taxonomy__popover :deep(input) { width: 100%; height: 36px; padding: 0 10px; border: 1px solid $ui-line-strong; border-radius: 8px; outline: none; color: $ui-ink; font: 400 13px $ui-font-body; }
.ticket-taxonomy__popover :deep(input:focus) { border-color: $ui-blue; box-shadow: 0 0 0 3px rgba($ui-blue, .1); }
.ticket-taxonomy__palette,
.ticket-taxonomy__popover :deep(.ticket-taxonomy__picker-palette) { display: grid; grid-template-columns: repeat(6, 24px); gap: 8px; }
.ticket-taxonomy__popover :deep(.ticket-taxonomy__picker-palette) { margin: 10px 0; }
.ticket-taxonomy__palette button,
.ticket-taxonomy__popover :deep(.ticket-taxonomy__picker-palette button) { width: 24px; height: 24px; padding: 0; border: 2px solid #fff; border-radius: 50%; cursor: pointer; box-shadow: 0 0 0 1px rgba(16, 24, 40, .14); }
.ticket-taxonomy__palette button.is-selected,
.ticket-taxonomy__popover :deep(.ticket-taxonomy__picker-palette button.is-selected) { box-shadow: 0 0 0 2px $ui-ink; }
.ticket-taxonomy__popover :deep(.ticket-taxonomy__options) { display: grid; max-height: 230px; overflow-y: auto; border-top: 1px solid $ui-line; }
.ticket-taxonomy__popover :deep(.ticket-taxonomy__options > button) { display: grid; min-height: 36px; align-items: center; grid-template-columns: 12px minmax(0, 1fr) auto; gap: 8px; padding: 6px 4px; border: 0; border-bottom: 1px solid $ui-line; background: transparent; color: $ui-ink; cursor: pointer; font: 500 13px $ui-font-body; text-align: left; }
.ticket-taxonomy__popover :deep(.ticket-taxonomy__options > button:hover) { background: $ui-surface-soft; }
.ticket-taxonomy__popover :deep(.ticket-taxonomy__options i) { width: 10px; height: 10px; border-radius: 50%; }
.ticket-taxonomy__popover :deep(.ticket-taxonomy__options kbd) { color: $ui-muted; font: 500 10px $ui-font-body; }
.ticket-taxonomy__popover :deep(.ticket-taxonomy__options p) { margin: 10px 2px 2px; color: $ui-muted; font-size: 12px; }
@media (max-width: 780px) {
  .ticket-taxonomy { align-items: flex-start; flex-direction: column; }
  .ticket-taxonomy__divider { width: 100%; height: 1px; }
  .ticket-taxonomy__popover { position: fixed; top: 50%; left: 50%; width: calc(100vw - 32px); max-width: 286px; transform: translate(-50%, -50%); }
}
</style>
